import React, { useState } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../App';
import './AlphaSignupModal.css';

interface AlphaSignupModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

interface SignupData {
  email: string;
  location: string;
  source: string;
  useCase: string;
  acceptedTerms: boolean;
}

export const AlphaSignupModal: React.FC<AlphaSignupModalProps> = ({ isOpen, onClose, onSuccess }) => {
  const [step, setStep] = useState<'form' | 'consent' | 'success'>('form');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  const [formData, setFormData] = useState<SignupData>({
    email: '',
    location: '',
    source: '',
    useCase: '',
    acceptedTerms: false
  });

  const locations = [
    { value: 'us', label: 'United States' },
    { value: 'eu', label: 'European Union' },
    { value: 'uk', label: 'United Kingdom' },
    { value: 'other', label: 'Other' }
  ];

  const sources = [
    { value: 'search', label: 'Search Engine' },
    { value: 'social', label: 'Social Media' },
    { value: 'friend', label: 'Friend/Colleague' },
    { value: 'other', label: 'Other' }
  ];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    // Validate US-only for now
    if (formData.location !== 'us') {
      setError('Alpha testing is currently only available in the United States. Join our waitlist to be notified when we expand!');
      return;
    }

    // Validate email
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setError('Please enter a valid email address');
      return;
    }

    // Show consent step
    setStep('consent');
  };

  const handleFinalSubmit = async () => {
    if (!formData.acceptedTerms) {
      setError('Please accept the alpha testing terms to continue');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await axios.post(`${API_BASE_URL}/api/alpha/signup`, {
        ...formData,
        timestamp: new Date().toISOString()
      });

      if (response.data.status === 'success') {
        setStep('success');
        // Auto close after 5 seconds
        setTimeout(() => {
          onSuccess();
        }, 5000);
      } else {
        setError(response.data.message || 'Failed to submit signup');
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="alpha-signup-overlay">
      <div className="alpha-signup-modal">
        {step === 'form' && (
          <>
            <div className="modal-header">
              <h2>Request Alpha Access</h2>
              <button className="close-btn" onClick={onClose}>×</button>
            </div>
            
            <div className="modal-content">
              <p className="intro-text">
                Join our exclusive alpha testing program and help shape the future of health data management.
                Limited spots available!
              </p>

              <form onSubmit={handleSubmit}>
                <div className="form-group">
                  <label htmlFor="email">Email Address *</label>
                  <input
                    type="email"
                    id="email"
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    placeholder="your@email.com"
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="location">Location *</label>
                  <select
                    id="location"
                    value={formData.location}
                    onChange={(e) => setFormData({...formData, location: e.target.value})}
                    required
                  >
                    <option value="">Select your location</option>
                    {locations.map(loc => (
                      <option key={loc.value} value={loc.value}>{loc.label}</option>
                    ))}
                  </select>
                  {formData.location && formData.location !== 'us' && (
                    <p className="location-note">
                      🌍 Alpha testing is currently US-only. You'll be added to our waitlist for {locations.find(l => l.value === formData.location)?.label}.
                    </p>
                  )}
                </div>

                <div className="form-group">
                  <label htmlFor="source">How did you hear about us?</label>
                  <select
                    id="source"
                    value={formData.source}
                    onChange={(e) => setFormData({...formData, source: e.target.value})}
                  >
                    <option value="">Select an option</option>
                    {sources.map(src => (
                      <option key={src.value} value={src.value}>{src.label}</option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="useCase">What would you like to track? (Optional)</label>
                  <textarea
                    id="useCase"
                    value={formData.useCase}
                    onChange={(e) => setFormData({...formData, useCase: e.target.value})}
                    placeholder="E.g., cholesterol levels, thyroid function, general health markers..."
                    rows={3}
                  />
                </div>

                {error && (
                  <div className="error-message">
                    {error}
                  </div>
                )}

                <div className="form-actions">
                  <button type="button" className="btn-secondary" onClick={onClose}>
                    Cancel
                  </button>
                  <button type="submit" className="btn-primary">
                    Continue
                  </button>
                </div>
              </form>
            </div>
          </>
        )}

        {step === 'consent' && (
          <>
            <div className="modal-header">
              <h2>Alpha Testing Agreement</h2>
            </div>
            
            <div className="modal-content">
              <div className="consent-box">
                <h3>Before you join our alpha program:</h3>
                
                <div className="consent-section">
                  <div className="consent-icon">🚧</div>
                  <div className="consent-details">
                    <h4>Early Access Software</h4>
                    <ul>
                      <li>This is early-stage software that may have bugs</li>
                      <li>Features may change or be removed without notice</li>
                      <li>There may be occasional downtime</li>
                    </ul>
                  </div>
                </div>

                <div className="consent-section">
                  <div className="consent-icon">🔒</div>
                  <div className="consent-details">
                    <h4>Your Data is Protected</h4>
                    <ul>
                      <li>Your data is secure and HIPAA compliant</li>
                      <li>You can delete your data at any time</li>
                      <li>We never share your health information</li>
                    </ul>
                  </div>
                </div>

                <div className="consent-section">
                  <div className="consent-icon">💬</div>
                  <div className="consent-details">
                    <h4>Your Feedback Matters</h4>
                    <ul>
                      <li>Your feedback will directly influence development</li>
                      <li>Help us build the best health tracking tool</li>
                      <li>Get lifetime benefits as an early supporter</li>
                    </ul>
                  </div>
                </div>

                <div className="medical-disclaimer">
                  <div className="disclaimer-icon">⚕️</div>
                  <div className="disclaimer-content">
                    <strong>Important Medical Disclaimer:</strong>
                    <p>
                      UnderstandMyResults is an educational tool designed to help you track and understand your lab results. 
                      It is not a substitute for professional medical advice, diagnosis, or treatment. Always consult with 
                      your healthcare provider about your lab results and before making any medical decisions.
                    </p>
                  </div>
                </div>

                <div className="consent-checkbox">
                  <label>
                    <input
                      type="checkbox"
                      checked={formData.acceptedTerms}
                      onChange={(e) => setFormData({...formData, acceptedTerms: e.target.checked})}
                    />
                    <span>
                      I understand and accept the alpha testing terms and medical disclaimer. I agree to the{' '}
                      <a href="/terms" target="_blank">Terms of Service</a> and{' '}
                      <a href="/privacy" target="_blank">Privacy Policy</a>.
                    </span>
                  </label>
                </div>
              </div>

              {error && (
                <div className="error-message">
                  {error}
                </div>
              )}

              <div className="form-actions">
                <button 
                  type="button" 
                  className="btn-secondary" 
                  onClick={() => setStep('form')}
                  disabled={loading}
                >
                  Back
                </button>
                <button 
                  type="button" 
                  className="btn-primary" 
                  onClick={handleFinalSubmit}
                  disabled={loading || !formData.acceptedTerms}
                >
                  {loading ? 'Submitting...' : 'Join Alpha Program'}
                </button>
              </div>
            </div>
          </>
        )}

        {step === 'success' && (
          <>
            <div className="modal-content success-state">
              <div className="success-icon">🎉</div>
              <h2>Welcome to the Alpha Program!</h2>
              <p>
                Thank you for signing up! We'll review your application and send you 
                an email within 24-48 hours with your access details.
              </p>
              <p className="success-note">
                Check your email ({formData.email}) for a confirmation message.
              </p>
              <button className="btn-primary" onClick={onSuccess}>
                Got it!
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};